<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm One-Time Service Day Change</div>

            <form novalidate @submit.prevent="createOneTimeServiceDayChange">
                <LsDatepicker
                    v-model="date"
                    track-id="one_time_service_day_change_view-date"
                    name="date"
                    label="Select preferred date"
                    class="ls-flow--330"
                    :validation="{ required: true }"
                    :min-date="minDate"
                    :unselectable-days-of-week="unselectableDaysOfWeek"
                />

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="one_time_service_day_change_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="one_time_service_day_change_view-confirm_change"
                    text="Confirm Change"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment-timezone';
import { lsErrorService, lsModalService } from 'ls-vue-common';
import gql from 'graphql-tag';
import { lsHelpers } from '@/helpers';
import CreateOneTimeServiceDayChange from '@/graphql/mutations/CreateOneTimeServiceDayChange.gql';
import { LsButton, LsInput, LsDatepicker } from '@/components/common';
import { FlowSuccessModal } from '@/modals';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import { apolloClient } from '@/vue-apollo';
import LsError from '@/components/common/LsError/LsError.vue';
import Schedule from '@/graphql/queries/Schedule.gql';
import ScheduleOneTimeServiceDayChangeDetails from '@/graphql/queries/ScheduleOneTimeServiceDayChangeDetails.gql';

export default {
    name: 'OneTimeServiceDayChangeView',
    components: {
        LsButton,
        LsInput,
        LsTemplatedLoading,
        LsError,
        LsDatepicker,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
            isInvalid: false,
            minDate: moment()
                .startOf('day')
                .add(1, 'days')
                .toDate(),

            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                    }
                `,
            }),

            schedule: apolloClient.readFragment({
                id: `Schedule:${this.$route.params.schedule_id}`,
                fragment: gql`
                    fragment schedule on Schedule {
                        next_customer_starts_at
                        next_customer_ends_at
                    }
                `,
            }),

            unselectableDaysOfWeek: [0, 6],

            variables: {
                customer_id: _.toNumber(this.$route.params.customer_id),
                property_id: _.toNumber(this.$route.params.property_id),
                schedule_id: _.toNumber(this.$route.params.schedule_id),
            },

            // form data
            date: null,
            zendesk_ticket_id: '',
        };
    },
    apollo: {
        propertyDayOfWeekAvailability: {
            query: ScheduleOneTimeServiceDayChangeDetails,
            variables() {
                return this.variables;
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.propertyDayOfWeekAvailability.skip = false;
                lsHelpers.goToParentView({ replace: true });
            },
            result({ data, loading }) {
                if (!loading) {
                    this.unselectableDaysOfWeek = lsHelpers.getUnselectableDaysOfWeekFromDayOfWeekAvailability(
                        data.propertyDayOfWeekAvailability
                    );
                }
            },
            notifyOnNetworkStatusChange: true,
            loadingKey: 'loading',
            fetchPolicy: 'network-only',
        },
    },
    methods: {
        async createOneTimeServiceDayChange() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            const payload = {
                date: moment(this.date).format('YYYY-MM-DD'),
                requested_is_contractor_flexible: false,
                zendesk_ticket_id: this.zendesk_ticket_id
                    ? _.toInteger(this.zendesk_ticket_id)
                    : null,
                reason: 'event',
            };

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateOneTimeServiceDayChange,
                    variables: {
                        ...this.variables,
                        ...payload,
                    },
                    refetchQueries: [
                        {
                            query: Schedule,
                            variables: this.variables,
                        },
                    ],
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Service changed for ${this.property.street}`,
                            copy: data.createOneTimeServiceDayChange.copy,
                            displayServicesOnDate: {
                                next_customer_starts_at: this.schedule.next_customer_starts_at,
                                next_customer_ends_at: this.schedule.next_customer_ends_at,
                            },
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-one-time-service-day-change-view--requested-is-contractor-flexible {
    margin-bottom: 5px;
    margin-top: 20px;
}
</style>
